import type { TableStyles } from 'react-data-table-component';
import { VXTableProps } from '../../components';
import { fontFamily } from '../components/typography';
import { buildPalette } from '../palette';
import { theme } from '../theme';

const palette = buildPalette();

const typographyStyle = {
  fontFamily: fontFamily,
  fontStyle: 'normal',
  fontWeight: 400,
  fontHeight: '150%',
};

const fontSize = (dense: boolean) => (dense ? '12px' : '16px');
const padding = (dense: boolean) => (dense ? '0 12px' : '0 18px');
const margin = (dense: boolean) => (dense ? '2px 0' : '4px 0');
const minHeight = (dense: boolean) => (dense ? '52px' : '64px');

export const tableStyles = (variant: VXTableProps<unknown>['variant'] = 'standard', dense = false): TableStyles => {
  if (variant === 'basic') return basicTableStyles(dense);
  return standardTableStyles(dense);
};

const commonStyles = (dense: boolean): TableStyles => ({
  head: {
    style: {
      color: palette.primary?.[60],
    },
  },
  headCells: {
    style: {
      ...typographyStyle,
      fontSize: fontSize(dense),
      padding: padding(dense),
    },
  },
  headRow: {
    style: {
      border: 0,
      backgroundColor: palette.primary?.[10],
    },
  },
  rows: {
    style: {
      backgroundColor: 'white',
      borderRadius: '4px',
      margin: '5px 0',
      '&&': { border: '0' },
    },
    highlightOnHoverStyle: {
      backgroundColor: palette.primary?.[20],
      outline: 0,
    },
  },
  cells: {
    style: {
      ...typographyStyle,
      color: palette.primary?.main,
      fontSize: fontSize(dense),
      padding: padding(dense),
      minHeight: minHeight(dense),
    },
  },
  pagination: {
    style: {
      marginTop: '16px',
    },
  },
  expanderCell: {
    style: {
      order: 1000,
    },
  },
  expanderButton: {
    style: {
      '&:hover:not(:disabled), &:focus': {
        backgroundColor: 'transparent',
      },
    },
  },
  expanderRow: {
    style: {
      background: theme.palette.common.white,
    },
  },
  subHeader: {
    style: {
      display: 'block',
      backgroundColor: 'transparent',
      padding: '4px',
    },
  },
});

export const standardTableStyles = (dense: boolean): TableStyles => ({
  ...commonStyles(dense),
  table: {
    style: {
      backgroundColor: 'transparent',
    },
  },
  head: {
    style: {
      zIndex: 2,
    },
  },
  headRow: {
    style: {
      border: 0,
      backgroundColor: palette.primary?.[10],
    },
  },
  rows: {
    style: {
      backgroundColor: 'white',
      borderRadius: '4px',
      margin: margin(dense),
      '&&': { border: '0' },
    },
    highlightOnHoverStyle: {
      backgroundColor: palette.primary?.[20],
      outline: 0,
    },
  },
});

export const basicTableStyles = (dense: boolean): TableStyles => ({
  ...commonStyles(dense),
  headRow: {
    style: {
      borderBottom: `1px dashed ${palette.primary?.[40]}!important`,
    },
  },
  rows: {
    style: {
      borderBottom: `1px dashed ${palette.primary?.[40]}!important`,
    },
    highlightOnHoverStyle: {
      backgroundColor: palette.primary?.[10],
    },
  },
});
